import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { SETTINGS_TABS } from '@/Pages/tabRoutes';
import SpacesPanel from './components/SpacesPanel';
// The one panel that lives outside `Settings/components`: a rate card is a
// feature with its own API module, its own refusal vocabulary and its own
// dialogs, and `Pages/Pricing` is where those already are. Settings is the
// place it is *reached* from, not the place it is implemented.
import PricingListsPanel from '@/Pages/Pricing/PricingListsPanel';
import StaffPanel from './components/StaffPanel';
import RolesPanel from './components/RolesPanel';
import ScopeAssignmentsPanel from './components/ScopeAssignmentsPanel';
import BranchesPanel from './components/BranchesPanel';
import DevicesPanel from './components/DevicesPanel';
import WifiPanel from './components/WifiPanel';
import GuestSettingsPanel from './components/GuestSettingsPanel';
import BookingWidgetPanel from './components/BookingWidgetPanel';
import PlanPanel from './components/PlanPanel';
// The other panel that lives outside `Settings/components`, for the same
// reason `PricingListsPanel` does above: ADR-0027's marketplace is its own
// feature with its own API module and dialogs, and `Pages/DevPortal` is where
// it already is. Settings is where it is *reached* from, not where it is
// implemented. The developer portal half of ADR-0027 (registering an app,
// submitting versions, sandbox provisioning) is now its own application —
// a venue operator installs apps, they do not author them — so this screen
// no longer offers a tab for it; see `Pages/DevPortal/components/DeveloperAppsPanel.tsx`
// and `DeveloperAppDetail.tsx`, left in place unreferenced for that app to lift.
import MarketplacePanel from '@/Pages/DevPortal/components/MarketplacePanel';


/**
 * P1-08 / P1-09 — the tenant administration surface: staff, roles and their
 * permission matrix, scoped role assignments, branches and devices.
 *
 * Local state plus the `api` client, in the shape `Pages/Spaces/FloorMap.tsx`
 * uses: none of this is shared with another screen, so none of it belongs in
 * the store.
 */
export default function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const routed = useRoutedTabs('/settings', SETTINGS_TABS);

  /**
   * Which person the assignments tab should open on, when it was reached from
   * "manage roles" in the staff list. In the query string rather than component
   * state so the link is shareable and survives a reload — the whole point of
   * putting the tab in the URL is lost if the thing you were looking at is not.
   */
  const focusedStaffId = searchParams.get('staff');
  const setFocusedStaffId = (userId: string | null) => {
    setSearchParams(
      (params) => {
        if (userId) params.set('staff', userId);
        else params.delete('staff');
        return params;
      },
      { replace: true },
    );
  };

  const openAssignments = (userId: string) => {
    navigate(`/settings/assignments?staff=${encodeURIComponent(userId)}`);
  };

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('settings.title')}</h1>
        <p className="text-sm text-ink-muted">{t('settings.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('settings.title')}
        idPrefix="settings"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'spaces' && <SpacesPanel />}
      {current === 'pricing' && <PricingListsPanel />}
      {current === 'staff' && <StaffPanel onManageRoles={openAssignments} />}
      {current === 'roles' && <RolesPanel />}
      {current === 'assignments' && (
        <ScopeAssignmentsPanel initialStaffId={focusedStaffId} onStaffChange={setFocusedStaffId} />
      )}
      {current === 'branches' && <BranchesPanel />}
      {current === 'devices' && <DevicesPanel />}
      {current === 'wifi' && <WifiPanel />}
      {current === 'guest' && <GuestSettingsPanel />}
      {current === 'booking' && <BookingWidgetPanel />}
      {current === 'plan' && <PlanPanel />}
      {current === 'marketplace' && <MarketplacePanel />}
    </div>
  );
}
